// Global Instructions Rule Applied!
//
// Fund full-form Chart of Accounts tab: the setup screen behind the Ledger and
// Statements tabs.
//
// A fund's chart is its set of acc_chart_of_accounts rows. Without one,
// booking fails outright - ft__coa_for_item raises when an accounting item has
// no fund-scoped account, so a capital call on such a fund cannot book at all.
// Until this tab existed the only remedy was running
// acc__ensure_chart_of_accounts by hand.
//
// Two grids:
//   GL Accounts    - which catalogue accounts the fund uses. Staged edits with
//                    an explicit Save (the FeePeriodsEditor recipe).
//   Item Mapping   - which GL account each accounting item books to. An item
//                    with NO journals is a staged edit like the above; an item
//                    WITH journals opens the reallocation modal instead,
//                    because changing it migrates real accounting history and
//                    should never be batched behind a generic Save button.
//
// Reallocation is hybrid by design: open financial years are restated in
// place, closed years are never touched and their balance moves via a
// reclassification journal on a date the user picks. See acc__reallocate_item.
//
// Lightweight fetch-and-render recipe (LedgerTab / CapitalCallsTab precedent):
// tab-local state, no redux slice.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Input, Modal, Select, Switch, Table, Tag, Tooltip, Typography } from 'antd'
import DatePicker from '../../../../../core/components/DatePicker.tsx'
import { PlusOutlined, ReloadOutlined, SaveOutlined, WarningOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'
import {
  fetchFundChartOfAccounts,
  fetchFundChartOptions,
  createFundChartFull,
  copyFundChartFromFund,
  createFundChartFromTemplate,
  saveFundChartAccounts,
  reallocateFundChartItem,
  saveFundChartAsTemplate
} from '../../../services/fundsService.ts'
import type {
  FundChartAccountRow,
  FundChartItemRow,
  ChartGlAccountOption,
  ChartSourceFundOption,
  ChartTemplateOption
} from '../../../../../state_manager/data_models/fund_chart_of_accounts.ts'

const { Text } = Typography

const DATE_FORMAT = 'MM/DD/YYYY'
const disp = (v: unknown): string => (v != null && v !== '' ? String(v) : '-')

interface ChartOfAccountsTabProps {
  fundId: string | null
}

export default function ChartOfAccountsTab({ fundId }: ChartOfAccountsTabProps) {
  const [accounts, setAccounts] = useState<FundChartAccountRow[]>([])
  const [items, setItems] = useState<FundChartItemRow[]>([])
  const [hasChart, setHasChart] = useState(false)
  const [firstOpenFyStart, setFirstOpenFyStart] = useState<string | null>(null)
  const [glAccounts, setGlAccounts] = useState<ChartGlAccountOption[]>([])
  const [sourceFunds, setSourceFunds] = useState<ChartSourceFundOption[]>([])
  const [templates, setTemplates] = useState<ChartTemplateOption[]>([])
  const [loading, setLoading] = useState(false)

  // staged edits, keyed by the id the save payload needs
  const [activeDraft, setActiveDraft] = useState<Record<string, boolean>>({})
  const [mappingDraft, setMappingDraft] = useState<Record<string, string>>({})
  const [savingAccounts, setSavingAccounts] = useState(false)
  const [savingMappings, setSavingMappings] = useState(false)

  // creation controls (empty state)
  const [creating, setCreating] = useState(false)
  const [sourceFundId, setSourceFundId] = useState<string | null>(null)
  const [templateId, setTemplateId] = useState<string | null>(null)

  // reallocation modal
  const [realloc, setRealloc] = useState<{ item: FundChartItemRow; newGlAccountId: string } | null>(null)
  const [reclassDate, setReclassDate] = useState<string | null>(null)
  const [reallocating, setReallocating] = useState(false)

  // save-as-template modal
  const [templateModalOpen, setTemplateModalOpen] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [savingTemplate, setSavingTemplate] = useState(false)

  const load = useCallback(async () => {
    if (!fundId) return
    setLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const [chart, options] = await Promise.all([
        fetchFundChartOfAccounts(fundId, token),
        fetchFundChartOptions(fundId, token)
      ])
      if (chart.error) showErrorToast(chart.error)
      if (options.error) showErrorToast(options.error)
      setAccounts(chart.accounts)
      setItems(chart.items)
      setHasChart(chart.hasChart)
      setFirstOpenFyStart(chart.firstOpenFyStart)
      setGlAccounts(options.glAccounts)
      setSourceFunds(options.sourceFunds)
      setTemplates(options.templates)
      setActiveDraft({})
      setMappingDraft({})
    } finally {
      setLoading(false)
    }
  }, [fundId])

  useEffect(() => {
    void load()
  }, [load])

  const accountLabel = useCallback(
    (glAccountId: string | null | undefined): string => {
      if (!glAccountId) return '—'
      const opt = glAccounts.find((o) => o.value === glAccountId)
      return opt ? opt.label : glAccountId
    },
    [glAccounts]
  )

  const accountsDirty = Object.keys(activeDraft).length > 0
  const mappingsDirty = Object.keys(mappingDraft).length > 0

  const runCreate = useCallback(
    async (action: () => Promise<{ data: { status_message?: string } | null; error?: string }>) => {
      setCreating(true)
      try {
        const res = await action()
        if (res.error) {
          showErrorToast(res.error)
          return
        }
        toast.success(res.data?.status_message || 'Chart of accounts created')
        await load()
      } finally {
        setCreating(false)
      }
    },
    [load]
  )

  const handleSaveAccounts = useCallback(async () => {
    if (!fundId) return
    setSavingAccounts(true)
    try {
      const token = getAccessToken(store.getState().auth)
      // full grid state, draft merged over what the server last returned
      const payload = accounts.map((a) => ({
        gl_account_id: a.cfg_acc_gl_accounts__id,
        active: activeDraft[a.cfg_acc_gl_accounts__id] ?? a.prog__active
      }))
      const res = await saveFundChartAccounts(fundId, payload, token)
      if (res.error) {
        // the server refuses deactivations that would strand journals or break
        // an item's resolution - keep the drafts so the user can undo the one
        // it named and re-save
        showErrorToast(res.error)
        return
      }
      toast.success(res.data?.status_message || 'Chart accounts saved')
      await load()
    } finally {
      setSavingAccounts(false)
    }
  }, [fundId, accounts, activeDraft, load])

  // Items with no journals migrate nothing, so their mapping change is just a
  // mapping change and batches behind Save. acc__reallocate_item is still the
  // write - it is the single place that owns item -> account resolution.
  const handleSaveMappings = useCallback(async () => {
    if (!fundId) return
    setSavingMappings(true)
    try {
      const token = getAccessToken(store.getState().auth)
      for (const [itemId, glAccountId] of Object.entries(mappingDraft)) {
        const res = await reallocateFundChartItem(
          fundId,
          { item_id: itemId, new_gl_account_id: glAccountId, reclass_date: null },
          token
        )
        if (res.error) {
          showErrorToast(res.error)
          await load()
          return
        }
      }
      toast.success('Item mappings saved')
      await load()
    } finally {
      setSavingMappings(false)
    }
  }, [fundId, mappingDraft, load])

  const handleMappingChange = useCallback(
    (row: FundChartItemRow, glAccountId: string) => {
      const hasJournals = row.prog__journal_count_open + row.prog__journal_count_closed > 0
      if (!hasJournals) {
        setMappingDraft((prev) => ({ ...prev, [row.cfg_acc_items__id]: glAccountId }))
        return
      }
      // journals exist: this is a migration, not an edit
      setReclassDate(row.prog__journal_count_closed > 0 ? firstOpenFyStart : null)
      setRealloc({ item: row, newGlAccountId: glAccountId })
    },
    [firstOpenFyStart]
  )

  const handleReallocate = useCallback(async () => {
    if (!fundId || !realloc) return
    if (realloc.item.prog__journal_count_closed > 0 && !reclassDate) {
      showErrorToast('Choose a reclassification date for the closed-year balance')
      return
    }
    setReallocating(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await reallocateFundChartItem(
        fundId,
        {
          item_id: realloc.item.cfg_acc_items__id,
          new_gl_account_id: realloc.newGlAccountId,
          reclass_date: realloc.item.prog__journal_count_closed > 0 ? reclassDate : null
        },
        token
      )
      if (res.error) {
        showErrorToast(res.error)
        return
      }
      toast.success(res.data?.status_message || 'Accounting item reallocated')
      setRealloc(null)
      await load()
    } finally {
      setReallocating(false)
    }
  }, [fundId, realloc, reclassDate, load])

  const handleSaveAsTemplate = useCallback(async () => {
    if (!fundId) return
    setSavingTemplate(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await saveFundChartAsTemplate(fundId, templateName.trim(), token)
      if (res.error) {
        // name collisions come back as 422 - keep the modal open so the name
        // can be edited rather than retyped
        showErrorToast(res.error)
        return
      }
      toast.success(res.data?.status_message || 'Template saved')
      setTemplateModalOpen(false)
      setTemplateName('')
      await load()
    } finally {
      setSavingTemplate(false)
    }
  }, [fundId, templateName, load])

  // account rows grouped by account type with section header rows
  type AccountDisplayRow =
    | ({ kind: 'account' } & FundChartAccountRow)
    | { kind: 'section'; cfg_acc_gl_accounts__id: string; label: string }

  const accountRows: AccountDisplayRow[] = useMemo(() => {
    const out: AccountDisplayRow[] = []
    let lastType: string | null = null
    for (const a of accounts) {
      const type = a.cfg_acc_account_types__label ?? 'Other'
      if (type !== lastType) {
        out.push({ kind: 'section', cfg_acc_gl_accounts__id: `section-${type}`, label: type })
        lastType = type
      }
      out.push({ kind: 'account', ...a })
    }
    return out
  }, [accounts])

  const unresolvedCount = useMemo(() => items.filter((i) => !i.prog__resolves).length, [items])

  // Most funds that need this screen have a PARTIAL chart (a single account,
  // not none), so they never see the empty state's creation controls. Without
  // this they would have to flip every remaining switch by hand.
  const inactiveCount = useMemo(() => accounts.filter((a) => !a.prog__active).length, [accounts])

  const accountColumns = [
    {
      title: 'Account',
      dataIndex: 'acc_gl_accounts__account_name',
      render: (_: unknown, row: AccountDisplayRow) =>
        row.kind === 'section' ? (
          <Text strong className='text-gray-600'>
            {row.label}
          </Text>
        ) : (
          <span style={{ paddingLeft: 12 }}>
            {row.acc_gl_accounts__account_code ? `${row.acc_gl_accounts__account_code} · ` : ''}
            {row.acc_gl_accounts__account_name}
          </span>
        )
    },
    {
      title: 'Journals',
      dataIndex: 'prog__journal_count',
      width: 110,
      align: 'right' as const,
      render: (_: unknown, row: AccountDisplayRow) =>
        row.kind === 'section' ? '' : row.prog__journal_count > 0 ? (
          row.prog__journal_count
        ) : (
          <Text type='secondary'>0</Text>
        )
    },
    {
      title: 'Balance',
      dataIndex: 'prog__balance_fmt',
      width: 150,
      align: 'right' as const,
      render: (_: unknown, row: AccountDisplayRow) => (row.kind === 'section' ? '' : disp(row.prog__balance_fmt))
    },
    {
      title: 'Active',
      dataIndex: 'prog__active',
      width: 90,
      align: 'center' as const,
      render: (_: unknown, row: AccountDisplayRow) => {
        if (row.kind === 'section') return ''
        const checked = activeDraft[row.cfg_acc_gl_accounts__id] ?? row.prog__active
        const control = (
          <Switch
            size='small'
            checked={checked}
            onChange={(v) =>
              setActiveDraft((prev) => ({ ...prev, [row.cfg_acc_gl_accounts__id]: v }))
            }
          />
        )
        // the server owns the refusal rule; this only forewarns
        return row.prog__journal_count > 0 && checked ? (
          <Tooltip title={`${row.prog__journal_count} journal line(s) — deactivation will be refused until the accounting items are reallocated`}>
            {control}
          </Tooltip>
        ) : (
          control
        )
      }
    }
  ]

  const itemColumns = [
    {
      title: 'Accounting item',
      dataIndex: 'cfg_acc_items__label',
      render: (_: unknown, row: FundChartItemRow) => (
        <span>
          {row.cfg_acc_items__label}
          {row.cfg_acc_items__call_component ? (
            <Tag className='ml-2' color='default'>
              call component
            </Tag>
          ) : null}
          {!row.prog__resolves ? (
            <Tooltip title='This item has no account in this fund — anything booking it will fail. Pick an account to fix it.'>
              <Tag className='ml-2' color='red' icon={<WarningOutlined />}>
                unmapped
              </Tag>
            </Tooltip>
          ) : null}
        </span>
      )
    },
    {
      title: 'Source',
      dataIndex: 'prog__source',
      width: 110,
      render: (_: unknown, row: FundChartItemRow) =>
        row.prog__source === 'override' ? (
          <Tag color='blue'>Override</Tag>
        ) : row.prog__source === 'global' ? (
          <Tag>Default</Tag>
        ) : (
          <Tag color='red'>None</Tag>
        )
    },
    {
      title: 'GL account',
      dataIndex: 'prog__effective_gl_account_id',
      width: 320,
      render: (_: unknown, row: FundChartItemRow) => (
        <Select
          size='small'
          className='w-full'
          showSearch
          optionFilterProp='label'
          placeholder='Select account...'
          value={mappingDraft[row.cfg_acc_items__id] ?? row.prog__effective_gl_account_id ?? undefined}
          options={glAccounts}
          onChange={(v) => handleMappingChange(row, v)}
        />
      )
    },
    {
      title: 'Journals',
      dataIndex: 'prog__journal_count_open',
      width: 160,
      align: 'right' as const,
      render: (_: unknown, row: FundChartItemRow) =>
        row.prog__journal_count_open + row.prog__journal_count_closed === 0 ? (
          <Text type='secondary'>—</Text>
        ) : (
          <Text>
            {row.prog__journal_count_open} open / {row.prog__journal_count_closed} closed
          </Text>
        )
    }
  ]

  if (!fundId) {
    return <div className='p-6 text-gray-500'>Save the fund first to set up its chart of accounts.</div>
  }

  // ---- empty state: no chart yet -----------------------------------------
  if (!hasChart && !loading) {
    return (
      <div className='h-full overflow-y-auto p-3'>
        <FormSection title='Create Chart of Accounts' titleColor='blue' loading={loading}>
          <Text type='secondary' className='block mb-4'>
            This fund has no chart of accounts, so nothing can be booked against it — capital calls,
            fund transactions and asset transactions will all fail until it has one.
          </Text>

          <div className='flex flex-col gap-4'>
            <div className='flex items-center gap-3'>
              <Button type='primary' loading={creating} onClick={() => void runCreate(() => createFundChartFull(fundId, getAccessToken(store.getState().auth)))}>
                Create full chart
              </Button>
              <Text type='secondary'>Activate every account in the GL catalogue.</Text>
            </div>

            {sourceFunds.length > 0 ? (
              <div className='flex items-center gap-3'>
                <Select
                  className='w-72'
                  showSearch
                  allowClear
                  optionFilterProp='label'
                  placeholder='Copy from fund...'
                  value={sourceFundId}
                  options={sourceFunds.map((f) => ({
                    value: f.value,
                    label: `${f.label} (${f.account_count ?? 0} accounts)`
                  }))}
                  onChange={(v) => setSourceFundId(v ?? null)}
                />
                <Button
                  disabled={!sourceFundId}
                  loading={creating}
                  onClick={() => void runCreate(() => copyFundChartFromFund(fundId, sourceFundId as string, getAccessToken(store.getState().auth)))}
                >
                  Copy chart
                </Button>
                <Text type='secondary'>Copy another fund&apos;s accounts and item mappings.</Text>
              </div>
            ) : null}

            {templates.length > 0 ? (
              <div className='flex items-center gap-3'>
                <Select
                  className='w-72'
                  showSearch
                  allowClear
                  optionFilterProp='label'
                  placeholder='Use a template...'
                  value={templateId}
                  options={templates.map((t) => ({
                    value: t.value,
                    label: `${t.label} (${t.account_count ?? 0} accounts)`
                  }))}
                  onChange={(v) => setTemplateId(v ?? null)}
                />
                <Button
                  disabled={!templateId}
                  loading={creating}
                  onClick={() => void runCreate(() => createFundChartFromTemplate(fundId, templateId as string, getAccessToken(store.getState().auth)))}
                >
                  Create from template
                </Button>
                <Text type='secondary'>Use a saved chart template.</Text>
              </div>
            ) : null}
          </div>
        </FormSection>
      </div>
    )
  }

  return (
    <div className='h-full overflow-y-auto p-3'>
      <FormSection
        title='GL Accounts'
        titleColor='blue'
        loading={loading}
        headerActions={
          <div className='flex items-center gap-2'>
            {inactiveCount > 0 ? (
              <Tooltip title='Adds every catalogue account this fund has never had. Accounts you deactivated stay off.'>
                <Button
                  size='small'
                  icon={<PlusOutlined />}
                  loading={creating}
                  onClick={() =>
                    void runCreate(() => createFundChartFull(fundId, getAccessToken(store.getState().auth)))
                  }
                >
                  Add missing accounts
                </Button>
              </Tooltip>
            ) : null}
            <Button
              type='primary'
              size='small'
              icon={<SaveOutlined />}
              disabled={!accountsDirty}
              loading={savingAccounts}
              onClick={() => void handleSaveAccounts()}
            >
              Save Accounts
            </Button>
            <Tooltip title={accountsDirty || mappingsDirty ? 'Save pending changes first — a template snapshots the saved chart' : ''}>
              <Button
                size='small'
                icon={<PlusOutlined />}
                disabled={accountsDirty || mappingsDirty}
                onClick={() => setTemplateModalOpen(true)}
              >
                Save as template
              </Button>
            </Tooltip>
            <Button size='small' icon={<ReloadOutlined />} onClick={() => void load()}>
              Refresh
            </Button>
          </div>
        }
      >
        <Table
          size='small'
          columns={accountColumns}
          dataSource={accountRows}
          pagination={false}
          rowKey='cfg_acc_gl_accounts__id'
        />
        <Text type='secondary' className='block mt-2'>
          Deactivating an account is refused while it still carries journal lines, or while any
          accounting item below still books to it.
        </Text>
      </FormSection>

      <FormSection
        title='Accounting Item Mapping'
        titleColor='orange'
        loading={loading}
        headerActions={
          <Button
            type='primary'
            size='small'
            icon={<SaveOutlined />}
            disabled={!mappingsDirty}
            loading={savingMappings}
            onClick={() => void handleSaveMappings()}
          >
            Save Mappings
          </Button>
        }
      >
        {unresolvedCount > 0 ? (
          <div className='mb-2'>
            <Tag color='red' icon={<WarningOutlined />}>
              {unresolvedCount} item{unresolvedCount === 1 ? '' : 's'} without an account in this fund
            </Tag>
          </div>
        ) : null}
        <Table
          size='small'
          columns={itemColumns}
          dataSource={items}
          pagination={false}
          rowKey='cfg_acc_items__id'
        />
        <Text type='secondary' className='block mt-2'>
          Default means the item follows the global mapping; Override means this fund books it
          somewhere else. Changing an item that already has journals opens the reallocation dialog —
          it moves that history, so it is applied immediately rather than batched.
        </Text>
      </FormSection>

      {/* Reallocation: a controlled Modal rather than modal.confirm because it
          needs a DatePicker for the closed-year reclassification date. */}
      <Modal
        open={realloc !== null}
        title={realloc ? `Reallocate “${realloc.item.cfg_acc_items__label}”` : ''}
        okText='Reallocate'
        okButtonProps={{ danger: true }}
        confirmLoading={reallocating}
        onOk={() => void handleReallocate()}
        onCancel={() => setRealloc(null)}
        destroyOnClose
      >
        {realloc ? (
          <div className='flex flex-col gap-3'>
            <div>
              <div>
                <Text type='secondary'>From:</Text>{' '}
                {accountLabel(realloc.item.prog__effective_gl_account_id)}
              </div>
              <div>
                <Text type='secondary'>To:</Text> {accountLabel(realloc.newGlAccountId)}
              </div>
            </div>

            <ul className='list-disc pl-5 m-0'>
              <li>
                {realloc.item.prog__journal_count_open > 0
                  ? `${realloc.item.prog__journal_count_open} journal line(s) in open financial years will be restated to the new account.`
                  : 'No open-year journal lines are affected.'}
              </li>
              {realloc.item.prog__journal_count_closed > 0 ? (
                <li>
                  {realloc.item.prog__journal_count_closed} line(s) in closed financial years keep
                  their account. Their remaining balance moves by a single reclassification journal
                  booked on:
                  <div className='mt-2'>
                    <DatePicker
                      value={reclassDate ? dayjs(reclassDate) : null}
                      format={DATE_FORMAT}
                      onChange={(d) => setReclassDate(d ? d.format('YYYY-MM-DD') : null)}
                    />
                  </div>
                </li>
              ) : null}
            </ul>

            <Text type='secondary'>
              This changes booked accounting records. Open-year trial balances and statements will
              show the new account; closed periods keep what was already reported.
            </Text>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={templateModalOpen}
        title='Save chart as template'
        okText='Save template'
        confirmLoading={savingTemplate}
        okButtonProps={{ disabled: !templateName.trim() }}
        onOk={() => void handleSaveAsTemplate()}
        onCancel={() => setTemplateModalOpen(false)}
        destroyOnClose
      >
        <Text type='secondary' className='block mb-2'>
          Snapshots this fund&apos;s active accounts and item mappings as a reusable template.
        </Text>
        <Input
          autoFocus
          maxLength={120}
          placeholder='Template name'
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          onPressEnter={() => {
            if (templateName.trim()) void handleSaveAsTemplate()
          }}
        />
      </Modal>
    </div>
  )
}
